import React, { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import imageCompression from 'browser-image-compression';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { 
  Upload, 
  FileText, 
  Trash2, 
  Download, 
  Loader2,
  Paperclip,
  Image,
  File,
  FileSpreadsheet,
  FileCode,
  Eye,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { resolveFileUrl } from '../lib/fileUtils';
import { cn } from '../lib/utils';
import { extractDroppedFiles, OUTLOOK_DROP_HINT } from '../lib/dropFiles';

const API = process.env.REACT_APP_BACKEND_URL;

// Image compression options
// Tightened 2026-06-17 to match the per-form settings used by Locate /
// PreCon / SoftDig field-report uploads. Pre-tightening, an iPhone photo
// dropped into a Job Files dropzone landed on OneDrive at ~1 MB while
// the same photo uploaded inside a field report landed at ~250 KB — same
// network, same storage, no reason for the asymmetry. These settings
// produce ~250 KB JPEGs at 1280 px on the longest side, which is
// sufficient quality for job-site photos, equipment shots, and
// scanned conditions. PDFs and other non-image files are bypassed
// entirely (see isCompressibleImage below) and continue to upload
// at their original size.
const compressionOptions = {
  maxSizeMB: 0.3,           // 300 KB cap per photo
  maxWidthOrHeight: 1280,   // 1280 px on the longest side
  useWebWorker: true,       // run in a Web Worker to keep UI responsive
  fileType: 'image/jpeg',   // convert PNG / HEIC / HEIF to JPEG
  initialQuality: 0.82,     // visually indistinguishable from quality 90
};

// Check if file is an image that can be compressed
const isCompressibleImage = (file) => {
  const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
  return imageTypes.includes(file.type.toLowerCase());
};

// Compress image file
const compressImage = async (file) => {
  try {
    const compressedBlob = await imageCompression(file, compressionOptions);
    // Convert Blob back to File to preserve the original filename
    const compressedFile = new File([compressedBlob], file.name, {
      type: compressedBlob.type || file.type,
      lastModified: Date.now(),
    });
    const compressionRatio = ((1 - compressedFile.size / file.size) * 100).toFixed(0);
    console.log(`Compressed ${file.name}: ${(file.size / 1024 / 1024).toFixed(2)}MB → ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB (${compressionRatio}% reduction)`);
    return compressedFile;
  } catch (error) {
    console.warn('Image compression failed, using original:', error);
    return file;
  }
};

// Check if file can be previewed in browser
const canPreviewFile = (filename, contentType) => {
  const ext = filename?.split('.').pop()?.toLowerCase();
  // Images
  if (contentType?.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) {
    return 'image';
  }
  // PDFs
  if (ext === 'pdf' || contentType === 'application/pdf') {
    return 'pdf';
  }
  // Text files
  if (['txt', 'csv', 'json', 'xml', 'md'].includes(ext) || contentType?.startsWith('text/')) {
    return 'text';
  }
  return false;
};

// Get file icon based on content type or extension
const getFileIcon = (filename, contentType) => {
  const ext = filename?.split('.').pop()?.toLowerCase();
  
  if (contentType?.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) {
    return <Image className="w-5 h-5 text-blue-500" />;
  }
  if (['pdf'].includes(ext)) {
    return <FileText className="w-5 h-5 text-red-500" />;
  }
  if (['xls', 'xlsx', 'csv'].includes(ext)) {
    return <FileSpreadsheet className="w-5 h-5 text-green-500" />;
  }
  if (['doc', 'docx', 'txt', 'rtf'].includes(ext)) {
    return <FileText className="w-5 h-5 text-blue-600" />;
  }
  if (['dwg', 'dxf', 'cad'].includes(ext)) {
    return <FileCode className="w-5 h-5 text-purple-500" />;
  }
  return <File className="w-5 h-5 text-slate-400" />;
};

// Format file size
const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Reusable File Upload Component
 * 
 * @param {Object} props
 * @param {Array} props.files - Array of file objects [{id, filename, stored_filename, content_type, size, uploaded_at, uploaded_by}]
 * @param {string} props.uploadEndpoint - API endpoint for uploading files (e.g., "/api/opportunities/123/files")
 * @param {string} props.deleteEndpoint - Base API endpoint for deleting files (e.g., "/api/opportunities/123/files")
 * @param {Function} props.onUploadSuccess - Callback after successful upload
 * @param {Function} props.onDeleteSuccess - Callback after successful delete
 * @param {string} props.title - Section title (default: "Attachments")
 * @param {boolean} props.canEdit - Whether user can upload/delete files
 * @param {string} props.accept - File types to accept (e.g., "image/*,.pdf")
 * @param {boolean} props.showCard - Wrap in Card component (default: true)
 * @param {string} props.emptyMessage - Message when no files
 */
export default function FileUpload({
  files = [],
  uploadEndpoint,
  deleteEndpoint,
  onUploadSuccess,
  onDeleteSuccess,
  title = "Attachments",
  canEdit = true,
  accept = "*/*",
  showCard = true,
  emptyMessage = "No files attached"
}) {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewType, setPreviewType] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);

  // Drag and drop handlers
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const uploadFiles = useCallback(async (selectedFiles) => {
    if (!selectedFiles?.length || !uploadEndpoint) return;

    setUploading(true);
    let successCount = 0;
    
    try {
      for (const file of selectedFiles) {
        // Check file size (25MB limit for original)
        if (file.size > 25 * 1024 * 1024) {
          toast.error(`"${file.name}" exceeds 25MB limit`);
          continue;
        }

        // Compress images before upload
        let fileToUpload = file;
        if (isCompressibleImage(file)) {
          toast.info(`Compressing ${file.name}...`);
          fileToUpload = await compressImage(file);
        }

        const formData = new FormData();
        formData.append('file', fileToUpload);

        await axios.post(`${API}${uploadEndpoint}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        successCount++;
      }
      
      if (successCount > 0) {
        toast.success(`${successCount} file${successCount > 1 ? 's' : ''} uploaded`);
        if (onUploadSuccess) onUploadSuccess();
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  }, [uploadEndpoint, onUploadSuccess]);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;

    if (!canEdit || uploading || !uploadEndpoint) return;

    // Use the shared extractor so Outlook-on-Windows drag quirks (where
    // files end up in dataTransfer.items but not .files) are handled
    // the same way across Jobs / Proposals / Field Reports.
    const { files: droppedFiles, hint } = extractDroppedFiles(e);
    if (droppedFiles.length === 0) {
      toast.error(
        hint === 'outlook-uri-only'
          ? OUTLOOK_DROP_HINT
          : 'No file detected in drop. Try selecting via the Upload button.',
        { duration: 7000 }
      );
      return;
    }

    await uploadFiles(droppedFiles);
  }, [canEdit, uploading, uploadEndpoint, uploadFiles]);

  const handleUpload = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    await uploadFiles(selectedFiles);
    e.target.value = ''; // Reset input
  };

  const handleDelete = async (fileId) => {
    if (!deleteEndpoint) return;
    
    setDeleting(fileId);
    try {
      await axios.delete(`${API}${deleteEndpoint}/${fileId}`);
      toast.success('File deleted');
      if (onDeleteSuccess) onDeleteSuccess();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete file');
    } finally {
      setDeleting(null);
    }
  };

  const getFileUrl = (file) => {
    // OneDrive files: always use proxy endpoint
    const resolved = resolveFileUrl(file);
    if (resolved) return resolved;
    if (file.stored_filename) {
      return `${API}/api/uploads/${file.stored_filename}`;
    }
    if (file.path) {
      return `${API}/api/files/${file.id}`;
    }
    return `${API}/api/files/${file.id}`;
  };

  const handlePreview = (file) => {
    const type = canPreviewFile(file.filename, file.content_type);
    if (type) {
      setPreviewFile(file);
      setPreviewType(type);
    } else {
      // Can't preview, just open in new tab
      window.open(getFileUrl(file), '_blank');
    }
  };

  const closePreview = () => {
    setPreviewFile(null);
    setPreviewType(null);
  };

  const content = (
    <div 
      className={cn(
        "space-y-3 relative transition-all duration-200 rounded-lg",
        isDragging && "ring-2 ring-amber-500 ring-offset-2"
      )}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Drag overlay */}
      {isDragging && (
        <div className="absolute inset-0 bg-amber-500/90 rounded-lg flex flex-col items-center justify-center z-10 pointer-events-none">
          <Upload className="w-10 h-10 text-white mb-2 animate-bounce" />
          <p className="text-white font-bold">Drop files here to upload</p>
        </div>
      )}
      
      {/* File List */}
      {files.length > 0 ? (
        <div className="space-y-2">
          {files.map(file => {
            const canPreview = canPreviewFile(file.filename, file.content_type);
            return (
              <div 
                key={file.id} 
                className={`flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200 hover:bg-slate-100 transition-colors group ${canPreview ? 'cursor-pointer' : ''}`}
                onClick={() => canPreview && handlePreview(file)}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  {getFileIcon(file.filename, file.content_type)}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {file.filename}
                      {canPreview && <Eye className="w-3 h-3 inline ml-2 text-slate-400" />}
                    </p>
                    <p className="text-xs text-slate-500">
                      {file.uploaded_by && <span>{file.uploaded_by}</span>}
                      {file.uploaded_at && <span> • {format(new Date(file.uploaded_at), 'MMM d, yyyy')}</span>}
                      {file.size && <span> • {formatFileSize(file.size)}</span>}
                    </p>
                  </div>
                </div>
                <div className="flex gap-1 opacity-70 group-hover:opacity-100" onClick={(e) => e.stopPropagation()}>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => window.open(getFileUrl(file), '_blank')}
                    title="Download"
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                  {canEdit && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleDelete(file.id)}
                      disabled={deleting === file.id}
                      title="Delete"
                    >
                      {deleting === file.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4 text-red-500" />
                    )}
                  </Button>
                )}
              </div>
            </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-6 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
          <Paperclip className="w-8 h-8 mx-auto mb-2 text-slate-300" />
          <p className="text-slate-500 text-sm">{emptyMessage}</p>
        </div>
      )}

      {/* Upload Button with drag hint */}
      {canEdit && (
        <label className="flex flex-col items-center justify-center gap-1 p-4 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-amber-500 hover:bg-amber-50/50 transition-colors">
          <input
            type="file"
            className="hidden"
            onChange={handleUpload}
            disabled={uploading}
            accept={accept}
            multiple
          />
          {uploading ? (
            <>
              <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
              <span className="text-sm text-amber-600 font-medium">Uploading...</span>
            </>
          ) : (
            <>
              <Upload className="w-6 h-6 text-slate-400" />
              <span className="text-sm text-slate-600 font-medium">Click to upload files</span>
              <span className="text-xs text-slate-400">or drag and drop</span>
            </>
          )}
        </label>
      )}
    </div>
  );

  if (!showCard) {
    return (
      <>
        {content}
        {/* File Preview Dialog */}
        <Dialog open={!!previewFile} onOpenChange={closePreview}>
          <DialogContent className="max-w-4xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {previewFile && getFileIcon(previewFile.filename, previewFile.content_type)}
                {previewFile?.filename}
              </DialogTitle>
            </DialogHeader>
            <div className="mt-4 overflow-auto max-h-[70vh]">
              {previewType === 'image' && previewFile && (
                <img 
                  src={getFileUrl(previewFile)} 
                  alt={previewFile.filename}
                  className="max-w-full h-auto mx-auto rounded-lg"
                />
              )}
              {previewType === 'pdf' && previewFile && (
                <iframe
                  src={getFileUrl(previewFile)}
                  title={previewFile.filename}
                  className="w-full h-[65vh] rounded-lg border"
                  frameBorder="0"
                />
              )}
              {previewType === 'text' && previewFile && (
                <iframe
                  src={getFileUrl(previewFile)}
                  title={previewFile.filename}
                  className="w-full h-[65vh] rounded-lg border bg-white"
                />
              )}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => window.open(getFileUrl(previewFile), '_blank')}>
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
              <Button variant="outline" onClick={closePreview}>
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <>
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
            <Paperclip className="w-5 h-5" />
            {title} ({files.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {content}
        </CardContent>
      </Card>
      
      {/* File Preview Dialog */}
      <Dialog open={!!previewFile} onOpenChange={closePreview}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {previewFile && getFileIcon(previewFile.filename, previewFile.content_type)}
              {previewFile?.filename}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 overflow-auto max-h-[70vh]">
            {previewType === 'image' && previewFile && (
              <img 
                src={getFileUrl(previewFile)} 
                alt={previewFile.filename}
                className="max-w-full h-auto mx-auto rounded-lg"
              />
            )}
            {previewType === 'pdf' && previewFile && (
              <iframe
                src={getFileUrl(previewFile)}
                title={previewFile.filename}
                className="w-full h-[65vh] rounded-lg border"
                frameBorder="0"
              />
            )}
            {previewType === 'text' && previewFile && (
              <iframe
                src={getFileUrl(previewFile)}
                title={previewFile.filename}
                className="w-full h-[65vh] rounded-lg border bg-white"
              />
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => window.open(getFileUrl(previewFile), '_blank')}>
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
            <Button variant="outline" onClick={closePreview}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
